import React, { useState, useEffect } from 'react';
import AddToFavourites from './AddToFavourites';

const MenuTabs = ({
  drawingId,
  breadcrumbs,
  threeDEnabled,
  layoutsEnabled,
  quoteBuilderV2Enabled,
  quoteBuilderEnabled,
  threeDPath,
  layoutsLandingPath,
  manufacturingCostCalculatorV2Path,
  dielinesApiDrawingTabPath,
  dielinesPath,
  noBookmark = false,
  activeTab = null,
  advancedOptionsFormVisible = false,
  rawArguments = null,
  onNavigate = null,
  comboGuestPresent = false,
  materialId = null
}) => {

  // ShowDrawing fires combo_state_changed whenever its activeCombosByGuestName
  // toggles, so the dieline page's tab label flips between "Nesting" and
  // "Nesting & Manufacturing Cost Calculator" live as the user adds/removes a
  // guest. The initial value comes from the comboGuestPresent prop (server
  // computes it from @nav_sections on the layouts page; defaults to false on
  // the dieline page until the first event fires).
  const [comboGuestPresentLive, setComboGuestPresentLive] = useState(comboGuestPresent);

  useEffect(() => {
    const handler = (event) => {
      setComboGuestPresentLive(Boolean(event.detail && event.detail.hasGuests));
    };
    document.addEventListener('combo_state_changed', handler);
    return () => { document.removeEventListener('combo_state_changed', handler); };
  }, []);

  // material_id 'custom' means the user typed their own thickness; version-1
  // pricing cannot cost it, so the tab drops "ve Üretim Maliyeti" and reads just
  // "Yerleştirme" (issue #5151). The id arrives as an explicit prop (the layout
  // and 3D server renders know it) or encoded inside rawArguments. On the dieline
  // page the user changes it live in MaterialChooser, which fires
  // 'material_id_changed', so we keep it in state and update on that event.
  const detectCustomMaterial = (id) => {
    if (id === 'custom') { return true; }
    const args = rawArguments || window.rawArguments || '';
    if (!args) { return false; }
    try {
      return new URLSearchParams(atob(args)).get('material_id') === 'custom';
    } catch (_e) {
      return false;
    }
  };

  const [isCustomMaterial, setIsCustomMaterial] = useState(detectCustomMaterial(materialId));

  useEffect(() => {
    const handler = (event) => {
      setIsCustomMaterial((event.detail && event.detail.material_id) === 'custom');
    };
    document.addEventListener('material_id_changed', handler);
    return () => { document.removeEventListener('material_id_changed', handler); };
  }, []);

  // Common helper to check if navigation should be blocked
  const checkNavigationBlocked = (e, requireLogin = true) => {
    // Check if user is logged in (if login is required)
    if (requireLogin && !window.loggedIn) {
      if (typeof emitLogInRequired === 'function') {
        emitLogInRequired();
      }
      return true;
    }

    return false;
  };

  // Helper to navigate with dieline options - triggers redraw first to validate
  const navigateWithDielineOptions = (action) => {
    // Store the pending navigation - DrawingSvg.onSuccess will handle navigation after redraw
    window.pendingMenuTabNavigation = {
      action: action,
      submit: 'dieline-options',
      advancedOptionsEnabled: advancedOptionsFormVisible
    };

    // Trigger redraw to validate values - navigation happens on success
    const redrawBtn = document.getElementById('redraw_btn');
    if (redrawBtn) {
      redrawBtn.click();
    }
  };

  // Helper to navigate with raw arguments
  const navigateWithRawArguments = (action) => {
    window.location.href = action + '/' + (rawArguments || window.rawArguments || '');
  };

  // The layouts URL stores all dieline options + combo state in a base64-encoded
  // path segment. The dieline page reads them from query params instead
  // (combo_ids directly, dieline options via options_json). When navigating
  // layouts -> dieline we have to translate the encoding so state survives.
  const navigateToDielineFromLayouts = (action) => {
    const args = rawArguments || window.rawArguments || '';
    if (!args) {
      window.location.href = action;
      return;
    }

    let decodedParams;
    try {
      decodedParams = new URLSearchParams(atob(args));
    } catch (_e) {
      window.location.href = action;
      return;
    }

    const queryParams = new URLSearchParams();

    if (decodedParams.has('combo_ids')) {
      queryParams.set('combo_ids', decodedParams.get('combo_ids'));
    }

    // ask_values[<rel>][<name>] and combo_materials[<rel>][<field>] are nested
    // bracket params; pass them through verbatim so Rails parses them as
    // params[:ask_values][rel][name] etc on the dieline page.
    decodedParams.forEach((value, key) => {
      if (key.startsWith('ask_values[') || key.startsWith('combo_materials[')) {
        queryParams.append(key, value);
      }
    });

    // Dieline options ride along as options_json so the controller's existing
    // @use_options branch (admin-only, which matches @combo_enabled gating)
    // picks them up. Drop combo state from the blob since it's already on the
    // URL, drop _ts (cache-buster), and drop any `[]`-suffixed array params
    // since URLSearchParams.forEach reports each occurrence separately and
    // JSON.stringify({"add_ons[]": value}) would silently lose all but the
    // last. Array params would need a different shape on the dieline side
    // anyway.
    const optionsObject = {};
    decodedParams.forEach((value, key) => {
      if (key === 'combo_ids') { return; }
      if (key === '_ts') { return; }
      if (key.endsWith('[]')) { return; }
      if (key.startsWith('ask_values[') || key.startsWith('combo_materials[')) { return; }
      optionsObject[key] = value;
    });
    if (Object.keys(optionsObject).length > 0) {
      queryParams.set('options_json', JSON.stringify(optionsObject));
    }

    const queryString = queryParams.toString();
    window.location.href = queryString.length > 0 ? `${action}?${queryString}` : action;
  };

  // Handler for breadcrumbs tab
  const handleBreadcrumbsClick = (e) => {
    e.preventDefault();

    // Don't navigate if already on breadcrumbs or no path available
    if (activeTab === 'breadcrumbs' || !dielinesPath) {
      return;
    }

    // Always require login for tab navigation
    if (checkNavigationBlocked(e, true)) {
      return;
    }

    // Custom navigation handler if provided
    if (onNavigate) {
      const submit = activeTab === 'dielines_api' ? 'direct' : 'dieline-options-from-layout';
      onNavigate(dielinesPath, submit, advancedOptionsFormVisible);
      return;
    }

    // Navigate based on where we're coming from
    if (activeTab === 'dielines_api') {
      window.location.href = dielinesPath;
    } else if (activeTab === 'layouts') {
      navigateToDielineFromLayouts(dielinesPath);
    } else {
      window.location.href = dielinesPath;
    }
  };

  // Handler for 3D Mockup tab
  const handle3DClick = (e) => {
    e.preventDefault();

    const action = e.currentTarget.dataset.action || threeDPath;

    if (!action || checkNavigationBlocked(e, true)) {
      return;
    }

    // Custom navigation handler if provided
    if (onNavigate) {
      const submitType = activeTab === 'dielines_api' ? 'dieline-options-from-api' : 'dieline-options';
      onNavigate(action, submitType, advancedOptionsFormVisible);
      return;
    }

    // Navigate based on current page
    if (activeTab === 'dielines_api' || activeTab === 'layouts') {
      navigateWithRawArguments(action);
    } else {
      navigateWithDielineOptions(action);
    }
  };

  // Handler for Layouts/Nesting tab
  const handleLayoutsClick = (e) => {
    e.preventDefault();

    const action = e.currentTarget.dataset.action || layoutsLandingPath;

    if (!action || activeTab === 'layouts' || checkNavigationBlocked(e, true)) {
      return;
    }

    // Custom navigation handler if provided
    if (onNavigate) {
      const submitType = activeTab === 'dielines_api' ? 'dieline-options-from-api' : 'dieline-options';
      onNavigate(action, submitType, advancedOptionsFormVisible);
      return;
    }

    // Navigate based on current page
    if (activeTab === 'dielines_api') {
      navigateWithRawArguments(action);
    } else {
      navigateWithDielineOptions(action);
    }
  };

  // Handler for Quote Builder V2 tab
  const handleQuoteBuilderV2Click = (e) => {
    e.preventDefault();

    const action = e.currentTarget.dataset.action || manufacturingCostCalculatorV2Path;

    if (!action || checkNavigationBlocked(e, true)) {
      return;
    }

    // Custom navigation handler if provided
    if (onNavigate) {
      onNavigate(action, 'dieline-options', advancedOptionsFormVisible);
      return;
    }

    // Always navigate with dieline options
    navigateWithDielineOptions(action);
  };

  // Handler for Dielines API tab
  const handleDielinesApiClick = (e) => {
    e.preventDefault();

    const action = e.currentTarget.dataset.action || dielinesApiDrawingTabPath;

    if (!action || activeTab === 'dielines_api') {
      return;
    }

    // Always require login for tab navigation
    if (checkNavigationBlocked(e, true)) {
      return;
    }

    // Custom navigation handler if provided
    if (onNavigate) {
      onNavigate(action, 'dieline-options', advancedOptionsFormVisible);
      return;
    }

    // Navigate with dieline options from any page
    navigateWithDielineOptions(action);
  };

  const getTabClassName = (tabName) => {
    const baseClass = 'shortcode_tab_item_title';
    const isActive = activeTab === tabName;
    return `${baseClass} ${isActive ? 'inactivetab' : 'active'}`;
  };

  return (
    <div className="container" id="menuTabs">
      <div className="row" style={{ borderLeft: '1px solid #eee' }}>
        <div className="col-lg-12" style={{ marginLeft: '-15px', marginBottom: '15px' }}>
          <div className="all_heads_cont">

            {!noBookmark && (
              <AddToFavourites defaultId={drawingId} />
            )}

            <div
              className={`shortcode_tab_item_title ${activeTab === 'breadcrumbs' ? 'inactivetab largeText16' : 'active'}`}
              style={{ borderLeft: '0px solid #eee', marginTop: '0px' }}
              onClick={handleBreadcrumbsClick}
            >
              <span id="header_breadcrumbs">{breadcrumbs}</span>
            </div>

            {threeDEnabled && (
              <div
                className={getTabClassName('3d')}
                id="menu_tab_3d"
                style={{ marginTop: '0px' }}
                onClick={handle3DClick}
              >
                {t('three_d.three_d_view')}
              </div>
            )}

            {layoutsEnabled && (
              <div
                className={getTabClassName('layouts')}
                id="menu_tab_nesting"
                style={{ marginTop: '0px' }}
                onClick={handleLayoutsClick}
              >
                {(quoteBuilderEnabled && !comboGuestPresentLive && !isCustomMaterial)
                  ? t('layouts.layouts_and_quote_builder_view')
                  : t('layouts.layouts_view')
                }
              </div>
            )}

            {quoteBuilderV2Enabled && !isCustomMaterial && (
              <div
                className={getTabClassName('quote_builder_v2')}
                id="menu_tab_nesting_v2"
                style={{ marginTop: '0px' }}
                onClick={handleQuoteBuilderV2Click}
              >
                Manufacturing Cost Calculator (V2)
              </div>
            )}

            <div
              className={`shortcode_tab_item_title ${activeTab === 'dielines_api' ? 'inactive largeText16' : 'active'}`}
              id="menu_tab_dielines_api"
              style={{ marginTop: '0px' }}
              onClick={handleDielinesApiClick}
            >
              {t('nav.dielines_api_for_developers')}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuTabs;